import React, { useMemo } from 'react';

import { PlaceholderPills } from '../PlaceholderPills';
import styles from '../styles.module.css';

import { Pill } from '@/components/Pill';
import { PlaceHolderShape } from '@/components/Placeholder';
import {
  ActivityTrackerEventType,
  ModuleNames,
} from '@/modules/activityTracker/constants';
import { useActivityTracker } from '@/modules/activityTracker/useActivityTracker';
import { buildPopularCategories } from '@/modules/categories/buildPopularCategories';
import { type PopularCategory } from '@/modules/categories/types';
import { useGroupsById } from '@/modules/categories/useGroupsById';
import { getDepartmentId } from '@/modules/drafts/helpers/categories';
import { type AxiosCompatibleResponse } from '@/modules/http/types';
import { type FetchFilterAggregatesResponse } from '@/modules/search/types';
import { useSearchFilterAggregates } from '@/modules/search/useSearchFilterAggregates';
import { useSearchFilters } from '@/modules/search/useSearchFilters';
import { useCurrentUser } from '@/modules/user/useCurrentUser';

export function PopularCategories() {
  const { data: groupsByIdRes, isLoading: isGroupsDataLoading } =
    useGroupsById();
  const {
    data: filterAggregatesData,
    isLoading: isFilterAggregateDataLoading,
  } = useSearchFilterAggregates({});
  const {
    searchFilters: { gender, groups, isKids, brands },
    setSearchFilters: { setGroupsFilter, setProductTypeFilter },
  } = useSearchFilters();
  const [_, user] = useCurrentUser();
  const { sendActivityTrackerEvent } = useActivityTracker({ userId: user?.id });
  const isLoading = isFilterAggregateDataLoading || isGroupsDataLoading;

  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const popularProductTypes = useMemo(() => {
    if (!groupsByIdRes || !filterAggregatesData?.data) {
      return [];
    }

    let key: keyof FetchFilterAggregatesResponse['departments'] =
      'everything_else';

    if (isKids) {
      key = 'kidswear';
    } else if (gender === 'male') {
      key = 'menswear';
    } else if (gender === 'female') {
      key = 'womenswear';
    }

    const departmentAggregates = (
      filterAggregatesData as AxiosCompatibleResponse<FetchFilterAggregatesResponse>
    ).data?.departments?.[key];

    if (!departmentAggregates) {
      return [];
    }

    return buildPopularCategories({
      groupId: groups,
      groupsById: groupsByIdRes.data,
      departmentAggregates,
    });
  }, [
    groups,
    isKids,
    gender,
    /*
     * Popular Categories is also shown on the Brand Category page
     * which fetches filter aggregate data that depends on brand and category selection
     **/
    brands?.length,
    isFilterAggregateDataLoading,
  ]);

  if (isLoading) {
    return <PlaceholderPills shape={PlaceHolderShape.PILL} />;
  }

  if (!popularProductTypes.length) {
    return null;
  }

  function handleClick({
    popularProductType,
    index,
  }: {
    popularProductType: PopularCategory;
    index: number;
  }) {
    sendActivityTrackerEvent(ActivityTrackerEventType.MODULE_CLICK_ACTION, {
      moduleName: ModuleNames.SubcategorySuggestions,
      schemaVersion: '3.0',
      itemClicked: popularProductType.slug,
      itemId: String(popularProductType.id),
      itemPosition: index,
    });

    if (popularProductType.parentId) {
      return setProductTypeFilter(popularProductType.id);
    }

    setGroupsFilter(getDepartmentId(gender, isKids), popularProductType.id);
  }

  return (
    <div className={styles.popularContainer}>
      <div className={styles.popularPillsContainer}>
        {popularProductTypes.map((popularProductType, index) => (
          <Pill
            key={popularProductType.id}
            onClick={() =>
              handleClick({
                popularProductType,
                index,
              })
            }
          >
            {popularProductType.name}
          </Pill>
        ))}
      </div>
    </div>
  );
}
